
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useHouses } from "@/hooks/useHouses";
import { useToast } from "@/hooks/use-toast";

export const usePlot = (plotId?: string) => {
  const { toast } = useToast();
  const { houses } = useHouses();
  
  const { data: plot, isLoading } = useQuery({
    queryKey: ["plot", houses, plotId],
    queryFn: async () => {
      // If specific plotId provided, fetch that plot
      if (plotId) {
        const { data, error } = await supabase
          .from('plots')
          .select('*')
          .eq('id', plotId)
          .single();
        
        if (error) {
          console.error('Error fetching plot by ID:', error);
          toast({
            title: "Error",
            description: "No se pudieron cargar los detalles de la parcela",
            variant: "destructive",
          });
          return null;
        }
        
        return data;
      }
      
      // If no plotId, use the original logic to find a plot based on user's houses
      // If user has no houses, return null
      if (!houses || houses.length === 0) return null;
      
      // Get the house IDs
      const houseIds = houses.map(house => house.id);
      
      // Find houses with plot_id
      const { data: housesWithPlots, error: housesError } = await supabase
        .from('houses')
        .select('id, name, plot_id')
        .in('id', houseIds)
        .not('plot_id', 'is', null);
      
      if (housesError) {
        console.error('Error fetching houses with plots:', housesError);
        toast({
          title: "Error",
          description: "No se pudieron cargar las casas con parcelas",
          variant: "destructive",
        });
        return null;
      }
      
      // If no houses have plots, return null
      if (!housesWithPlots || housesWithPlots.length === 0) return null;
      
      // Get plot IDs
      const plotIds = housesWithPlots.map(house => house.plot_id);
      
      // Fetch plot details
      const { data: plots, error: plotsError } = await supabase
        .from('plots')
        .select('*')
        .in('id', plotIds);
      
      if (plotsError) {
        console.error('Error fetching plots:', plotsError);
        toast({
          title: "Error",
          description: "No se pudieron cargar los detalles de la parcela",
          variant: "destructive",
        });
        return null;
      }
      
      // Return the first plot found (assuming a user's houses belong to one plot)
      return plots && plots.length > 0 ? plots[0] : null;
    },
    enabled: !!plotId || (houses && houses.length > 0),
  });
  
  return { plot, isLoading };
};
